/** Shared prompt routing and websocket handoff. */
import { useCallback } from "react";
import { useProjectStore } from "@/stores/project-store";
import { useFramesStore } from "@/stores/frames-store";
import { inputDestinationKey } from "@/lib/input-destination-key";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { checkpoint as traceCheckpoint } from "@/lib/execution-timer";
import {
  recordSessionLatency,
  traceDispatchReceipt,
} from "@/lib/session-latency-trace";
import { registerOutboundScreensForDispatch } from "@/desktop/outbound-screenshot-metadata";
import { isTauri } from "@/lib/is-tauri";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { tryRunDenkerModeCommand } from "@/lib/denker-mode-command";
import { runVoiceCommand } from "@/desktop/voice-read-aloud-command";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { isTransientConversationFrameMetadata } from "@/lib/transient-conversation-frame";
import { isDenkerInputTarget } from "@/lib/input-bar-target-kind";
import { registerLocalConversationFrameCorrelation } from "@/lib/local-conversation-frame-correlation";
import {
  clientRequestIdForPrompt,
  isPromptRequestRetained,
  markPendingPromptReceiptRetryableFailure,
  promptRetryIdentityForKey,
  rejectPendingPromptReceipt,
} from "@/lib/prompt-receipt-bus";
import { buildPromptDispatchRetryKey } from "@/lib/prompt-dispatch-retry-key";
import { destinationDraftComposerId } from "@/hooks/use-destination-draft";
import { attachMemoryFrameViews } from "@/lib/memory-frame-view";
import {
  bindPromptRetryEnvelopeRequest,
  clearPromptRetryEnvelopesForComposer,
  promptRetryEnvelopeForDraft,
} from "@/lib/prompt-retry-envelope";
import {
  claimPromptRoutingIntent,
  registerPromptRoutingClaim,
  waitForDispatchedPromptReceipt,
} from "@/lib/prompt-dispatch-settlement";
import {
  acknowledgeDispatchedAgentResult,
  reportDispatchUnavailable,
} from "@/lib/prompt-dispatch-feedback";
import type {
  DispatchUserMessageArgs,
  DispatchUserMessageResult,
} from "./prompt-dispatch-types";
export type {
  DispatchUserMessageArgs,
  DispatchUserMessageResult,
} from "./prompt-dispatch-types";

const NOT_ACCEPTED = Promise.resolve(false);

/**
 * The point new frames land near, only while the workspace view places
 * frames by position (the old canvas). On the wall the backend places them,
 * whatever position a caller passes (Part 06 Task 2).
 */
function dispatchViewportCenter(
  position: DispatchUserMessageArgs["position"],
): { x: number; y: number } | undefined {
  const hint = workspaceViewAdapter().viewportCenterHint();
  if (!hint) return undefined;
  const center = position ?? hint;
  return { x: Math.round(center.x), y: Math.round(center.y) };
}

/** A prompt the app ran itself - a mode or voice command - and sent nowhere. */
const localCommandHandled = (): DispatchUserMessageResult => ({
  resolvedAgentKey: undefined,
  sent: false,
  consumed: true,
  accepted: Promise.resolve(true),
  skippedReason: "local_command",
});

export function dispatchUserMessage(
  args: DispatchUserMessageArgs,
): DispatchUserMessageResult {
  const submittedRoutingIntent = args.ignoreInputBarSessionState
    ? (args.inputBarRoutingIntent ?? null)
    : (args.inputBarRoutingIntent ??
      useInputBarTarget.getState().snapshotRoutingIntent());
  const startsNewConversation = Boolean(
    args.startNewConversation || submittedRoutingIntent?.startNewConversation,
  );
  const submittedNewIntentId = startsNewConversation
    ? (submittedRoutingIntent?.intentId ?? null)
    : null;
  const retryEnvelope = args.composerId
    ? promptRetryEnvelopeForDraft(args.composerId, args.text)
    : null;
  const retryNewIntentId = retryEnvelope?.args.inputBarRoutingIntent?.intentId;
  const retryConflictsWithNewConversation = Boolean(
    retryEnvelope &&
    startsNewConversation &&
    (!submittedNewIntentId || retryNewIntentId !== submittedNewIntentId),
  );
  if (retryConflictsWithNewConversation && args.composerId) {
    clearPromptRetryEnvelopesForComposer(args.composerId);
  } else if (retryEnvelope) {
    args = {
      ...retryEnvelope.args,
      composerId: args.composerId,
      clientRequestId: retryEnvelope.clientRequestId,
    };
  }
  // "Voice off", "speak again", `/voice`: the app's to run, never the agent's;
  // what follows the command in the prompt goes on, and an answer to
  // Denker's question is never one (`voice-read-aloud-command.ts`).
  const voiceCommand =
    args.literalText || args.awaitingInputId
      ? null
      : runVoiceCommand(args.text);
  if (voiceCommand && !voiceCommand.rest) return localCommandHandled();
  if (voiceCommand) args = { ...args, text: voiceCommand.rest };
  const {
    text,
    position,
    agentKey: explicitAgentKey,
    imageIds,
    mentionedFrames,
    maxParallelAgents,
    originDisplayId,
    promptSource,
    conversationFrameId,
    clientRequestId: requestedClientRequestId,
    startNewConversation: forcedStartNewConversation,
    ignoreInputBarSessionState = false,
    inputBarRoutingIntent,
    execId: forcedExecId,
    conversationId: forcedConversationId,
    runtimeSessionId: forcedRuntimeSessionId,
    voiceAttention,
    screenRecordings,
    onboardingLesson,
  } = args;

  if (!args.literalText && tryRunDenkerModeCommand(text.trim())) {
    return localCommandHandled();
  }

  const websocket = getCurrentDenkerWs();
  if (!websocket) {
    reportDispatchUnavailable();
    return {
      resolvedAgentKey: undefined,
      sent: false,
      consumed: false,
      accepted: NOT_ACCEPTED,
      skippedReason: "missing_websocket",
    };
  }

  // The viewed project is only a hint, and may be absent: the server routes
  // by conversation, else to Denker's conversation in Home. A legacy mirrored
  // snapshot can briefly hold only the old scope, whose `project` is the id.
  const { currentProject, currentSpace } = useProjectStore.getState();
  const viewedProjectId =
    currentProject?.id ?? currentSpace?.project ?? currentSpace?.id;
  let routingIntent =
    inputBarRoutingIntent ??
    (ignoreInputBarSessionState
      ? null
      : useInputBarTarget.getState().snapshotRoutingIntent());
  const destinationKey = inputDestinationKey({
    startNewConversation:
      forcedStartNewConversation ?? routingIntent?.startNewConversation,
    conversationId:
      forcedConversationId ?? routingIntent?.target?.conversationId,
  });
  const composerId =
    args.composerId ??
    (conversationFrameId
      ? `conversation-frame:${conversationFrameId}`
      : destinationDraftComposerId(destinationKey));
  const promptRetryKey = buildPromptDispatchRetryKey(
    { ...args, composerId },
    destinationKey,
  );
  const retryIdentity = requestedClientRequestId
    ? null
    : promptRetryIdentityForKey(promptRetryKey, routingIntent);
  if (retryIdentity) routingIntent = retryIdentity.routingIntent;
  const explicitTarget = routingIntent?.target ?? null;
  // Typed @tokens are plain text; only explicit frame context is retained.
  const outboundText = text;
  const outboundMentionedFrames = mentionedFrames;
  // User chat is addressed to Denker without agent mention directives.
  const agentKey = "denker";
  // Resolve one exact durable Conversation. Runtime and execution ids are
  // supplemental only; neither may become a fallback chat address.
  let execId = forcedExecId;
  let conversationId = forcedConversationId;
  let runtimeSessionId = forcedRuntimeSessionId;
  let runtimeGeneration: number | undefined;
  let selectionGeneration: number | undefined;
  let routedConversationFrameId = conversationFrameId;
  let clientRequestId: string;
  const startNewConversation =
    forcedStartNewConversation ?? routingIntent?.startNewConversation ?? false;
  const knownRequestId =
    requestedClientRequestId ??
    retryIdentity?.requestId ??
    (startNewConversation ? (routingIntent?.intentId ?? undefined) : undefined);
  const routingClaim = claimPromptRoutingIntent({
    routingIntent,
    knownRequestId,
    retryStatus:
      retryIdentity?.status ??
      (requestedClientRequestId &&
      isPromptRequestRetained(requestedClientRequestId)
        ? "timed_out"
        : undefined),
  });
  if (!routingClaim.owned) {
    return {
      resolvedAgentKey: agentKey,
      sent: false,
      consumed: false,
      accepted: NOT_ACCEPTED,
      skippedReason: "routing_intent_claimed",
    };
  }
  if (startNewConversation) {
    execId = undefined;
    conversationId = undefined;
    runtimeSessionId = undefined;
  } else {
    const routeWasResolvedForAnotherAgent =
      !!explicitAgentKey && explicitAgentKey.toLowerCase() !== agentKey;
    if (routeWasResolvedForAnotherAgent) {
      execId = undefined;
      conversationId = undefined;
      runtimeSessionId = undefined;
      routedConversationFrameId = undefined;
    }

    if (isDenkerInputTarget(explicitTarget)) {
      if (explicitTarget.conversationId !== conversationId) {
        routedConversationFrameId = undefined;
      }
      conversationId = explicitTarget.conversationId;
      runtimeSessionId = explicitTarget.runtimeSessionId;
      runtimeGeneration = explicitTarget.runtimeGeneration;
      selectionGeneration = explicitTarget.selectionGeneration;
      execId = undefined;
    }
  }

  if (!conversationId) {
    // No durable target means ordinary targetless input. The backend resolves
    // the persisted manual selection (or atomically creates one). Only an
    // explicit New Chat intent may set startNewConversation.
    execId = undefined;
    runtimeSessionId = undefined;
    runtimeGeneration = undefined;
    selectionGeneration = undefined;
  }

  clientRequestId = clientRequestIdForPrompt({
    retryKey: promptRetryKey,
    composerId,
    routingIntent,
    requestedClientRequestId:
      requestedClientRequestId ??
      retryIdentity?.requestId ??
      (startNewConversation
        ? (routingIntent?.intentId ?? undefined)
        : undefined),
  });
  if (retryEnvelope) {
    bindPromptRetryEnvelopeRequest(composerId, clientRequestId);
  }
  registerPromptRoutingClaim(routingClaim, clientRequestId, routingIntent);

  const routedFrame = routedConversationFrameId
    ? useFramesStore.getState().framesById[routedConversationFrameId]
    : undefined;
  const localConversationFrame =
    routedFrame && isTransientConversationFrameMetadata(routedFrame.metadata)
      ? routedFrame
      : undefined;
  let registeredLocalCorrelation = false;
  if (localConversationFrame && viewedProjectId) {
    clientRequestId = registerLocalConversationFrameCorrelation({
      frameId: localConversationFrame.frameId,
      spaceId: viewedProjectId,
      ...(clientRequestId ? { requestId: clientRequestId } : {}),
    });
    registeredLocalCorrelation = true;
    routedConversationFrameId = undefined;
  }

  const retryEnvelopeArgs: DispatchUserMessageArgs = {
    ...args,
    text: outboundText,
    agentKey,
    mentionedAgents: [],
    mentionedFrames: outboundMentionedFrames ?? [],
    execId,
    conversationId,
    runtimeSessionId,
    conversationFrameId: routedConversationFrameId,
    startNewConversation,
    inputBarRoutingIntent: routingIntent ?? undefined,
    composerId,
  };

  const accepted = waitForDispatchedPromptReceipt({
    clientRequestId,
    retryKey: promptRetryKey,
    composerId,
    routingIntent,
    claim: routingClaim,
    registeredLocalCorrelation,
  });
  if (imageIds?.length) {
    registerOutboundScreensForDispatch({
      imageIds,
      clientRequestId,
      executionId: execId,
      sessionId: runtimeSessionId ?? undefined,
    });
  }
  try {
    const handoff = websocket.sendPrompt({
      ...(args.nativeChildInstruction ? { nativeChildInstruction: true } : {}),
      ...(args.awaitingInputId
        ? { awaitingInputId: args.awaitingInputId }
        : {}),
      projectId: viewedProjectId,
      text: outboundText,
      executionId: execId,
      viewportCenter: dispatchViewportCenter(position),
      imageIds: imageIds && imageIds.length > 0 ? imageIds : undefined,
      maxParallelAgents,
      // Live memory frame view is read at send time and stays out of the retry key.
      mentionedFrames:
        outboundMentionedFrames && outboundMentionedFrames.length > 0
          ? attachMemoryFrameViews(outboundMentionedFrames)
          : undefined,
      conversationId,
      runtimeSessionId,
      runtimeGeneration,
      selectionGeneration,
      originDisplayId:
        originDisplayId ??
        (isTauri()
          ? (useActiveDisplay.getState().activeDisplayId ??
            getWindowDisplayId())
          : null),
      promptSource,
      conversationFrameId: routedConversationFrameId,
      clientRequestId,
      startNewConversation,
      topicSplit: !args.keepConversation,
      voiceAttention,
      screenRecordings,
      onboardingLesson,
    });
    if (handoff instanceof Promise) {
      void handoff.catch((error: unknown) => {
        markPendingPromptReceiptRetryableFailure(
          clientRequestId,
          error instanceof Error ? error.message : String(error),
        );
      });
    }
  } catch (error) {
    rejectPendingPromptReceipt(
      clientRequestId,
      error instanceof Error ? error.message : String(error),
    );
    throw error;
  }
  acknowledgeDispatchedAgentResult({
    agentKey,
    spaceId: viewedProjectId,
    executionId: execId,
    sessionId: runtimeSessionId ?? undefined,
  });
  traceCheckpoint(execId ?? "_pending", "prompt_submitted");
  recordSessionLatency("dispatch.submitted", {
    clientRequestId,
    executionId: execId ?? undefined,
    imageIds,
  });
  traceDispatchReceipt(accepted, clientRequestId);

  return {
    resolvedAgentKey: agentKey,
    sent: true,
    consumed: true,
    accepted,
    clientRequestId,
    retryEnvelopeArgs,
  };
}

export function useDispatchUserMessage() {
  return useCallback(
    (args: DispatchUserMessageArgs): DispatchUserMessageResult =>
      dispatchUserMessage(args),
    [],
  );
}
